/**
 * Hook for the client-side recomposition of zone A "derived properties"
 * (spec 2026-05-05-03).
 *
 * The legacy backend endpoint `/equipment-tab` aggregated this data
 * server-side. Now we read the asset's property values via
 * `/assets/{uid}/properties` and project them into the same card shape the
 * UI consumed before — the rule library lives as data on the backend (in
 * the property-derivation pipeline that writes `asset_data` rows with
 * `source = "derivation"`), so the frontend doesn't re-evaluate rules; it
 * just merges the latest manual override with the latest derivation
 * result for every property the asset has values for.
 */

import { useMemo } from 'react'
import { useAssetProperties } from './useQueries'
import type { AssetPropertyValue } from './useQueries'
import type {
  DerivedPropertyCard,
  DerivedPropertyValue,
} from '../api/equipment-tab-types'

interface PropertyDefinitionLike {
  uid?: string
  code?: string
  name?: unknown
  type?: string
  data_type?: string
}

function getPropertyDefinition(p: AssetPropertyValue): PropertyDefinitionLike | undefined {
  // The API embeds the property definition under either `property_definition`
  // or directly on the value when fetched with the right flags.
  return (p as unknown as { property_definition?: PropertyDefinitionLike }).property_definition
}

function localized(name: unknown, fallback: string): string {
  if (typeof name === 'string') return name
  if (name && typeof name === 'object') {
    const m = name as Record<string, string>
    return m.fr || m.en || fallback
  }
  return fallback
}

interface PropertySource {
  /** "manual" | "derivation" | "import" | other backend source codes. */
  code: string
}

/**
 * Pull the source code from a property value. The Stonal API may surface this
 * via the embedded `source` relation (when it ships) or via the `source_details`
 * JSON. We tolerate both shapes.
 */
function getSourceCode(p: AssetPropertyValue): string {
  const src = (p as unknown as { source?: PropertySource }).source
  if (src && typeof src === 'object' && typeof src.code === 'string') return src.code
  const details = (p as unknown as { source_details?: { source?: string } }).source_details
  if (details && typeof details.source === 'string') return details.source
  return ''
}

export interface AssetDerivedPropertyCard extends DerivedPropertyCard {
  /** The property's UID — needed by the UI when issuing PUT/DELETE on the
   *  `/assets/{uid}/properties/{propertyUid}` endpoint. */
  property_uid?: string
}

/**
 * Returns a list of zone-A cards mirroring the legacy
 * EquipmentTabResponse.derived_properties shape, plus a `property_uid`
 * for each card so the UI can write manual overrides directly via the
 * existing /assets/{uid}/properties/{propertyUid} endpoint.
 */
export function useAssetDerivedProperties(org: string, assetUid: string) {
  const result = useAssetProperties(org, assetUid)
  const cards = useMemo<AssetDerivedPropertyCard[]>(() => {
    const properties = result.data ?? []
    type Bucket = { manual?: AssetPropertyValue; derivation?: AssetPropertyValue }
    const byCode = new Map<string, Bucket>()
    const order: string[] = []

    for (const p of properties) {
      const def = getPropertyDefinition(p)
      const code = def?.code
      if (!code) continue
      const sourceCode = getSourceCode(p)
      let bucket = byCode.get(code)
      if (!bucket) {
        bucket = {}
        byCode.set(code, bucket)
        order.push(code)
      }
      if (sourceCode === 'manual' && !bucket.manual) bucket.manual = p
      else if (sourceCode === 'derivation' && !bucket.derivation) bucket.derivation = p
    }

    const out: AssetDerivedPropertyCard[] = []
    for (const code of order) {
      const bucket = byCode.get(code)!
      const head = bucket.manual ?? bucket.derivation
      if (!head) continue
      const def = getPropertyDefinition(head)
      const label = localized(def?.name, code)
      const type = (def?.type ?? def?.data_type ?? 'text') as string
      const computed = bucket.derivation?.value as DerivedPropertyValue
      const manual = bucket.manual?.value as DerivedPropertyValue
      const isManual = !!bucket.manual
      const derivationUpdatedAt = (bucket.derivation as unknown as { updated_at?: string } | undefined)?.updated_at
      const manualUpdatedAt = (bucket.manual as unknown as { updated_at?: string } | undefined)?.updated_at
      out.push({
        key: code,
        label,
        type,
        state: isManual ? 'manual' : 'computed',
        computed_value: computed ?? null,
        manual_value: manual ?? null,
        displayed_value: (isManual ? manual : computed) ?? null,
        source_equipment: [],
        last_computed_at: derivationUpdatedAt ?? null,
        edited_by: null,
        edited_at: manualUpdatedAt ?? null,
        edited_reason: null,
        property_uid: def?.uid,
      })
    }
    out.sort((a, b) => a.key.localeCompare(b.key))
    return out
  }, [result.data])

  return {
    ...result,
    cards,
  }
}
